import React from 'react';

import { category, type LastUsedMethod } from '../../lastUsedWebAuthMethod';
import { useAccountForm } from '../AccountFormContext';
import { EmailModule } from './EmailModule';
import { OrDivider } from './primitives/OrDivider';
import { SocialAuthModule } from './SocialAuthModule';

interface EntryGroup {
  key: string;
  Group: React.FC;
}

/**
 * The entry step's two method groups, in default order: the provider stack above
 * the email field.
 *
 * A list rather than fixed siblings because the divider has to stay *between*
 * them whichever way round they render, and because the order is a decision the
 * last-used method makes.
 */
const SOCIAL_GROUP: EntryGroup = { key: 'social', Group: SocialAuthModule };
const EMAIL_GROUP: EntryGroup = { key: 'email', Group: EmailModule };

/**
 * Email above the providers when that is where the seller last signed in.
 *
 * The whole group swaps, not just the field: `EmailModule` is a single
 * `Form.Root`, so its field, CTA and "Forgot your password?" move together and
 * stay in one tab sequence. This reorders the DOM rather than applying a CSS
 * `order`, because DOM order *is* tab order — a visual-only swap would leave a
 * keyboard user tabbing through the stack in the order they can no longer see.
 */
const orderEntryGroups = (lastUsed: LastUsedMethod | undefined): readonly EntryGroup[] => {
  if (lastUsed !== undefined && category(lastUsed) === 'email') {
    return [EMAIL_GROUP, SOCIAL_GROUP];
  }
  return [SOCIAL_GROUP, EMAIL_GROUP];
};

/**
 * The entry step's method groups, ordered toward the returning seller's last-used
 * method and divided by an "Or".
 *
 * A component rather than an exported ordering function because the badge and
 * the order are two halves of one affordance: the modules draw a "Last used"
 * pill off context wherever they are mounted, so a surface that hand-placed the
 * groups would badge the email CTA while leaving it under the providers. The
 * modules stay individually exported for a surface that wants one group without
 * the other, so this is the composition to reach for rather than an invariant —
 * nothing detects a surface that places both by hand.
 */
export const EntryMethodGroups: React.FC = () => {
  const { lastUsedMethod } = useAccountForm();

  return (
    <>
      {orderEntryGroups(lastUsedMethod).map(({ key, Group }, index) => (
        <React.Fragment key={key}>
          {index > 0 ? <OrDivider /> : null}
          <Group />
        </React.Fragment>
      ))}
    </>
  );
};
